"use client";

import React, { useRef } from "react";
import Link from "next/link";
import { motion, useInView, useReducedMotion } from "framer-motion";
import FadeInView from "@/components/animations/FadeInView";
import StaggerContainer from "@/components/animations/StaggerContainer";

const primaryService = {
  id: 1,
  title: "Managed IT Services",
  description:
    "Proactive IT management and support for growing businesses. Your dedicated IT team without the overhead — monitoring, helpdesk, and full lifecycle management.",
  href: "/managed-it-services",
  accent: "#3B82F6",
  accentBg: "rgba(59,130,246,0.12)",
  borderAccent: "#5B21FF",
  stat: "24/7",
  statLabel: "Proactive Monitoring",
  includes: [
    "Helpdesk Support",
    "Remote Monitoring & Maintenance",
    "Device & Endpoint Management",
    "Patch Management",
    "Backup & Disaster Recovery",
    "Vendor Management",
    "Microsoft 365 Management",
    "User Support",
    "Managed Network Services",
  ],
  icon: (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      {/* Dashboard monitor */}
      <rect x="2" y="3" width="20" height="14" rx="2.5"/>
      <path d="M8 21h8M12 17v4"/>
      {/* Pulse/heartbeat line */}
      <path d="M6 10h3l1.5-3 2 6 1.5-3H18" strokeWidth="1.8"/>
      {/* Status dot */}
      <circle cx="18" cy="6" r="1.2" fill="currentColor" stroke="none"/>
    </svg>
  ),
};

const strategicService = {
  id: 2,
  title: "Infrastructure & Technology Deployment",
  description:
    "Designing and deploying reliable technology environments across business locations. From new office setups to multi-site network rollouts.",
  href: "/it-infrastructure",
  accent: "#22D3EE",
  accentBg: "rgba(34,211,238,0.1)",
  borderAccent: "#00D4FF",
  stat: "200+",
  statLabel: "Sites Deployed",
  includes: [
    "Network Solutions",
    "New Site Setup",
    "Data Centre & Server Infrastructure",
    "End-User Device Deployment",
    "POS & EFTPOS Systems",
    "ICT Procurement",
    "Multi-Site Infrastructure",
  ],
  icon: (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      {/* Server units */}
      <rect x="3" y="2" width="18" height="6" rx="2"/>
      <rect x="3" y="10" width="18" height="6" rx="2"/>
      {/* Status indicators */}
      <circle cx="6.5" cy="5" r="1" fill="currentColor" stroke="none"/>
      <circle cx="6.5" cy="13" r="1" fill="currentColor" stroke="none"/>
      <line x1="10" y1="5" x2="17" y2="5"/>
      <line x1="10" y1="13" x2="17" y2="13"/>
      {/* Connection lines to ground */}
      <path d="M8 18v3M12 18v3M16 18v3"/>
      <line x1="6" y1="21" x2="18" y2="21"/>
    </svg>
  ),
};

const supportingServices = [
  {
    id: 3,
    title: "Network Solutions",
    description: "Reliable, secure, and scalable network infrastructure. We design, deploy, and manage business networks for connectivity, security, and performance.",
    href: "/network-solutions",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        {/* Central hub */}
        <circle cx="12" cy="12" r="3"/>
        <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none"/>
        {/* Outer nodes */}
        <circle cx="12" cy="3" r="1.5"/>
        <circle cx="20" cy="17" r="1.5"/>
        <circle cx="4" cy="17" r="1.5"/>
        {/* Connecting lines */}
        <line x1="12" y1="4.5" x2="12" y2="9"/>
        <line x1="14.6" y1="14" x2="18.8" y2="16"/>
        <line x1="9.4" y1="14" x2="5.2" y2="16"/>
      </svg>
    ),
    accent: "#34D399",
    accentBg: "rgba(52,211,153,0.1)",
    borderAccent: "#00D4FF",
    stat: "99.9%",
    statLabel: "Network Uptime",
  },
  {
    id: 4,
    title: "Cybersecurity",
    description: "Protection across users, devices, networks, and infrastructure. Firewall protection, network segmentation, intrusion monitoring, and secure VPN connectivity.",
    href: "/cyber-security",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        {/* Modern shield */}
        <path d="M12 2L4 5.5v5.5c0 5.25 3.4 10.15 8 11.5 4.6-1.35 8-6.25 8-11.5V5.5L12 2z"/>
        {/* Lock icon inside */}
        <rect x="9.5" y="11" width="5" height="4" rx="1"/>
        <path d="M10.5 11V9.5a1.5 1.5 0 0 1 3 0V11"/>
        <circle cx="12" cy="13.2" r="0.6" fill="currentColor" stroke="none"/>
      </svg>
    ),
    accent: "#60A5FA",
    accentBg: "rgba(96,165,250,0.1)",
    borderAccent: "#00D4FF",
    stat: "0",
    statLabel: "Client Breaches",
  },
  {
    id: 5,
    title: "Cloud Solutions",
    description: "Microsoft Azure, Amazon AWS, and Microsoft 365 solutions for modern businesses.",
    href: "/cloud-solutions",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        {/* Cloud shape */}
        <path d="M6.5 19h11a4.5 4.5 0 0 0 .5-8.97A7 7 0 0 0 4.06 13.5 3.5 3.5 0 0 0 6.5 19z"/>
        {/* Sync arrows */}
        <path d="M9.5 13l2-2 2 2" strokeWidth="1.8"/>
        <path d="M13.5 16l-2 2-2-2" strokeWidth="1.8"/>
        <line x1="11.5" y1="11" x2="11.5" y2="16"/>
      </svg>
    ),
    accent: "#60A5FA",
    accentBg: "rgba(96,165,250,0.1)",
    borderAccent: "#00F0C0",
    stat: "3",
    statLabel: "Cloud Platforms",
  },
  {
    id: 6,
    title: "Business Communications",
    description: "VoIP / 3CX / Teams calling. Cloud phone systems and unified communications across all your sites.",
    href: "/business-communications",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        {/* Phone handset */}
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.362 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
        {/* Signal waves */}
        <path d="M14.5 2a8.38 8.38 0 0 1 5.5 5.5" strokeWidth="1.6"/>
        <path d="M14.5 5.5A4.5 4.5 0 0 1 18 8.5" strokeWidth="1.6"/>
      </svg>
    ),
    accent: "#22D3EE",
    accentBg: "rgba(34,211,238,0.1)",
    borderAccent: "#00D4FF",
    stat: "3CX",
    statLabel: "Certified Partner",
  },
  {
    id: 7,
    title: "AI & Automation Solutions",
    description: "Business process automation and AI-assisted systems. Reduce manual work and improve operational efficiency.",
    href: "/ai-automation",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        {/* Brain outline */}
        <path d="M12 2a5 5 0 0 0-4.8 3.6A4 4 0 0 0 4 9.5a4 4 0 0 0 1.5 3.1A4.5 4.5 0 0 0 9 17h2"/>
        <path d="M12 2a5 5 0 0 1 4.8 3.6A4 4 0 0 1 20 9.5a4 4 0 0 1-1.5 3.1A4.5 4.5 0 0 1 15 17h-2"/>
        {/* Circuit paths */}
        <line x1="12" y1="2" x2="12" y2="22"/>
        <path d="M9 8h6M8 12h8"/>
        {/* Nodes */}
        <circle cx="9" cy="8" r="0.8" fill="currentColor" stroke="none"/>
        <circle cx="15" cy="8" r="0.8" fill="currentColor" stroke="none"/>
        <circle cx="8" cy="12" r="0.8" fill="currentColor" stroke="none"/>
        <circle cx="16" cy="12" r="0.8" fill="currentColor" stroke="none"/>
      </svg>
    ),
    accent: "#A78BFA",
    accentBg: "rgba(167,139,250,0.1)",
    borderAccent: "#7C3AED",
    stat: "New",
    statLabel: "AI Solutions",
  },
  {
    id: 8,
    title: "ICT Procurement Services",
    description: "Technology sourcing and lifecycle management. We help businesses source, deploy, and manage the right technology solutions.",
    href: "/ict-procurement",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        {/* Shopping bag / procurement */}
        <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
        <line x1="3" y1="6" x2="21" y2="6"/>
        {/* Handle */}
        <path d="M16 10a4 4 0 0 1-8 0"/>
        {/* Checkmark */}
        <path d="M9.5 15l2 2 3.5-3.5" strokeWidth="1.8"/>
      </svg>
    ),
    accent: "#60A5FA",
    accentBg: "rgba(96,165,250,0.1)",
    borderAccent: "#5B21FF",
    stat: "Full",
    statLabel: "Lifecycle Mgmt",
  },
];

/* ── Staggered list item component ── */
function StaggeredList({ items, color, variant }: { items: string[]; color: string; variant: "check" | "diamond" }) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });
  const shouldReduceMotion = useReducedMotion();

  return (
    <div ref={ref} className={variant === "check" ? "grid grid-cols-1 gap-2" : "grid grid-cols-1 gap-2"}>
      {items.map((item, i) => (
        <motion.div
          key={item}
          initial={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, x: -12 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{
            duration: shouldReduceMotion ? 0.1 : 0.4,
            delay: shouldReduceMotion ? 0 : i * 0.06,
            ease: [0.16, 1, 0.3, 1],
          }}
        >
          {variant === "check" ? (
            <div className="flex items-center gap-2.5 text-[13px] font-body" style={{ color: "#CBD5E1" }}>
              <span className="w-4 h-4 rounded-full flex items-center justify-center shrink-0" style={{ background: `${color}33`, color }}>
                <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
              </span>
              {item}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-[13px] font-body px-3 py-2 rounded-xl" style={{ background: `${color}0D`, border: `1px solid ${color}1A`, color: "#CBD5E1" }}>
              <span style={{ color, fontSize: "10px" }}>◆</span>
              {item}
            </div>
          )}
        </motion.div>
      ))}
    </div>
  );
}

export default function ServicesBento() {
  const sectionRef = useRef<HTMLDivElement>(null);

  return (
    <section
      ref={sectionRef}
      className="relative pt-16 pb-12 sm:pt-20 sm:pb-12 overflow-hidden"
      style={{ background: "#0B1220" }}
    >
      {/* Animated radial bloom behind section */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ zIndex: 0 }}
      >
        <div
          className="services-bloom absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full"
          style={{
            background: "radial-gradient(circle, rgba(37,99,235,0.07) 0%, rgba(34,211,238,0.04) 40%, transparent 70%)",
            filter: "blur(60px)",
          }}
        />
        {/* Secondary drifting bloom */}
        <div
          className="blob-2 absolute top-1/3 left-1/4 w-[600px] h-[600px] rounded-full"
          style={{
            background: "radial-gradient(circle, rgba(34,211,238,0.04) 0%, transparent 60%)",
            filter: "blur(80px)",
          }}
        />
      </div>

      {/* Subtle section divider top */}
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "rgba(255,255,255,0.06)" }} />

      <div className="relative z-10 max-w-container mx-auto px-4 sm:px-6 w-full">
        {/* Header */}
        <FadeInView y={40} duration={0.8} delay={0} scale={0.97}>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
            <div>
              <div className="section-sep mb-5" />
              <h2
                className="font-heading font-700 leading-tight"
                style={{ fontSize: "clamp(32px, 4vw, 52px)", color: "#F8FAFC", letterSpacing: "-0.02em" }}
              >
                Built for Growing{' '}
                <br className="hidden sm:block" />
                <span className="gradient-text">New Zealand Businesses</span>
              </h2>
            </div>
            <p
              className="text-[17px] font-body md:max-w-[480px] md:text-right md:ml-auto leading-relaxed"
              style={{ color: "#CBD5E1" }}
            >
              From managed IT support and network infrastructure to cybersecurity, cloud platforms, communications, and automation — Pro IT Solutions delivers the complete technology foundation for modern New Zealand businesses.
            </p>
          </div>
        </FadeInView>

        {/* ── CORE SERVICES: Side-by-side full-width row ── */}
        <div className="mb-4">
          {/* Desktop: 2-col grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">

            {/* Core Card 1: Managed IT Services */}
            <FadeInView y={40} duration={0.7} delay={0.1} scale={0.96}>
              <Link
                href={primaryService?.href}
                className="group shimmer-border relative overflow-hidden rounded-2xl p-5 sm:p-8 flex flex-col h-full"
                style={{
                  background: "linear-gradient(135deg, rgba(37,99,235,0.15) 0%, rgba(11,18,32,0.95) 60%, rgba(17,24,39,0.9) 100%)",
                  border: "1px solid rgba(59,130,246,0.25)",
                  borderTop: `3px solid ${primaryService.borderAccent}`,
                  boxShadow: "0 0 60px rgba(37,99,235,0.1), inset 0 1px 0 rgba(255,255,255,0.12)",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  const el = e.currentTarget as HTMLAnchorElement;
                  el.style.borderColor = "rgba(59,130,246,0.5)";
                  el.style.borderTopColor = primaryService.borderAccent;
                  el.style.boxShadow = `0 0 40px ${primaryService.borderAccent}33, 0 0 80px rgba(59,130,246,0.2), inset 0 1px 0 rgba(255,255,255,0.12)`;
                  el.style.transform = "scale(1.02)";
                }}
                onMouseLeave={(e) => {
                  const el = e.currentTarget as HTMLAnchorElement;
                  el.style.borderColor = "rgba(59,130,246,0.25)";
                  el.style.borderTopColor = primaryService.borderAccent;
                  el.style.boxShadow = "0 0 60px rgba(37,99,235,0.1), inset 0 1px 0 rgba(255,255,255,0.12)";
                  el.style.transform = "scale(1)";
                }}
              >
                <div className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: "radial-gradient(ellipse at 20% 40%, rgba(37,99,235,0.15) 0%, transparent 60%)" }} />
                <div className="absolute top-6 right-6 px-3 py-1 rounded-full text-[11px] font-heading font-600 uppercase tracking-wider" style={{ background: "rgba(37,99,235,0.2)", border: "1px solid rgba(59,130,246,0.4)", color: "#60A5FA" }}>Core Service</div>
                <div className="relative z-10">
                  <div className="icon-breathe w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110" style={{ background: primaryService?.accentBg, color: primaryService?.accent }}>{primaryService?.icon}</div>
                  <h3 className="font-heading font-700 mb-3" style={{ fontSize: "clamp(22px, 2.2vw, 30px)", color: "#F8FAFC" }}>{primaryService?.title}</h3>
                  <p className="text-[14px] font-body leading-relaxed mb-6" style={{ color: "#94A3B8" }}>{primaryService?.description}</p>
                </div>
                <div className="relative z-10 flex-1">
                  <div className="p-5 rounded-xl" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
                    <p className="text-[11px] font-heading font-600 uppercase tracking-widest mb-4" style={{ color: "#64748B" }}>What&apos;s Included</p>
                    <StaggeredList items={primaryService?.includes || []} color="#60A5FA" variant="check" />
                  </div>
                </div>
                <div className="relative z-10 flex items-center gap-3 mt-auto pt-6">
                  <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[14px] font-heading font-600 transition-all duration-300 group-hover:gap-3" style={{ background: "rgba(37,99,235,0.2)", border: "1px solid rgba(59,130,246,0.35)", color: "#60A5FA" }}>
                    Explore Managed IT Services
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                  </div>
                  <div className="text-[13px] font-body" style={{ color: "#64748B" }}><span className="stat-glow" style={{ color: primaryService?.accent, fontWeight: 700 }}>{primaryService?.stat}</span> {primaryService?.statLabel}</div>
                </div>
              </Link>
            </FadeInView>

            {/* Core Card 2: Infrastructure */}
            <FadeInView y={40} duration={0.7} delay={0.2} scale={0.96}>
              <Link
                href={strategicService?.href}
                className="group shimmer-border relative overflow-hidden rounded-2xl p-5 sm:p-8 flex flex-col h-full"
                style={{
                  background: "linear-gradient(135deg, rgba(34,211,238,0.1) 0%, rgba(11,18,32,0.95) 60%, rgba(17,24,39,0.9) 100%)",
                  border: "1px solid rgba(34,211,238,0.2)",
                  borderTop: `3px solid ${strategicService.borderAccent}`,
                  boxShadow: "0 0 50px rgba(34,211,238,0.07), inset 0 1px 0 rgba(255,255,255,0.12)",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  const el = e.currentTarget as HTMLAnchorElement;
                  el.style.borderColor = "rgba(34,211,238,0.45)";
                  el.style.borderTopColor = strategicService.borderAccent;
                  el.style.boxShadow = `0 0 40px ${strategicService.borderAccent}33, 0 0 80px rgba(34,211,238,0.2), inset 0 1px 0 rgba(255,255,255,0.12)`;
                  el.style.transform = "scale(1.02)";
                }}
                onMouseLeave={(e) => {
                  const el = e.currentTarget as HTMLAnchorElement;
                  el.style.borderColor = "rgba(34,211,238,0.2)";
                  el.style.borderTopColor = strategicService.borderAccent;
                  el.style.boxShadow = "0 0 50px rgba(34,211,238,0.07), inset 0 1px 0 rgba(255,255,255,0.12)";
                  el.style.transform = "scale(1)";
                }}
              >
                <div className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: "radial-gradient(ellipse at 20% 40%, rgba(34,211,238,0.1) 0%, transparent 60%)" }} />
                <div className="absolute top-6 right-6 px-3 py-1 rounded-full text-[11px] font-heading font-600 uppercase tracking-wider" style={{ background: "rgba(34,211,238,0.1)", border: "1px solid rgba(34,211,238,0.3)", color: "#22D3EE" }}>Core Service</div>
                <div className="relative z-10">
                  <div className="icon-breathe w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110" style={{ background: strategicService?.accentBg, color: strategicService?.accent, animationDelay: "1s" }}>{strategicService?.icon}</div>
                  <h3 className="font-heading font-700 mb-3" style={{ fontSize: "clamp(22px, 2.2vw, 30px)", color: "#F8FAFC" }}>{strategicService?.title}</h3>
                  <p className="text-[14px] font-body leading-relaxed mb-6" style={{ color: "#94A3B8" }}>{strategicService?.description}</p>
                </div>
                <div className="relative z-10 flex-1">
                  <p className="text-[11px] font-heading font-600 uppercase tracking-widest mb-3" style={{ color: "#64748B" }}>Capabilities</p>
                  <StaggeredList items={strategicService?.includes || []} color="#22D3EE" variant="diamond" />
                </div>
                <div className="relative z-10 flex items-center gap-3 mt-auto pt-6">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-heading font-600 transition-all duration-300 group-hover:gap-3" style={{ background: "rgba(34,211,238,0.1)", border: "1px solid rgba(34,211,238,0.25)", color: "#22D3EE" }}>
                    Explore IT Infrastructure
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                  </div>
                  <div className="text-[13px] font-body" style={{ color: "#64748B" }}><span className="stat-glow" style={{ color: strategicService?.accent, fontWeight: 700 }}>{strategicService?.stat}</span> {strategicService?.statLabel}</div>
                </div>
              </Link>
            </FadeInView>
          </div>

        </div>

        {/* Supporting services label */}
        <FadeInView y={16} duration={0.5} delay={0.1}>
          <div className="mt-10 mb-4 flex items-center gap-4">
            <div className="h-px flex-1" style={{ background: "rgba(255,255,255,0.06)" }} />
            <span className="text-[11px] font-heading font-600 uppercase tracking-widest px-3" style={{ color: "#64748B" }}>Supporting Services</span>
            <div className="h-px flex-1" style={{ background: "rgba(255,255,255,0.06)" }} />
          </div>
        </FadeInView>

        {/* ── SUPPORTING: Desktop 3-col grid ── */}
        <StaggerContainer
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-stretch"
          staggerDelay={0.12}
          y={40}
          scale={0.95}
        >
          {supportingServices?.map((service) => (
            <Link
              key={service?.id}
              href={service?.href}
              className="group relative overflow-hidden rounded-2xl p-5 sm:p-7 flex flex-col h-full min-h-[280px]"
              style={{
                background: "linear-gradient(135deg, rgba(17,24,39,0.95) 0%, rgba(11,18,32,0.98) 100%)",
                border: "1px solid rgba(255,255,255,0.08)",
                borderTop: `3px solid ${service.borderAccent}`,
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.12)",
                transition: "all 0.3s ease",
              }}
              onMouseEnter={(e) => {
                const el = e.currentTarget as HTMLAnchorElement;
                el.style.borderColor = `${service.borderAccent}55`;
                el.style.borderTopColor = service.borderAccent;
                el.style.boxShadow = `0 0 30px ${service.borderAccent}25, 0 20px 60px ${service.borderAccent}15, inset 0 1px 0 rgba(255,255,255,0.12)`;
                el.style.transform = "scale(1.02)";
              }}
              onMouseLeave={(e) => {
                const el = e.currentTarget as HTMLAnchorElement;
                el.style.borderColor = "rgba(255,255,255,0.08)";
                el.style.borderTopColor = service.borderAccent;
                el.style.boxShadow = "inset 0 1px 0 rgba(255,255,255,0.12)";
                el.style.transform = "scale(1)";
              }}
            >
              <div className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: `radial-gradient(ellipse at 30% 30%, ${service?.accentBg} 0%, transparent 60%)` }} />
              <div className="icon-breathe relative z-10 w-11 h-11 rounded-2xl flex items-center justify-center mb-5 transition-transform duration-300 group-hover:scale-110" style={{ background: service?.accentBg, color: service?.accent, animationDelay: `${(service?.id || 0) * 0.5}s` }}>{service?.icon}</div>
              <div className="relative z-10 flex-1">
                <h3 className="font-heading font-600 text-[18px] mb-3 leading-snug" style={{ color: "#F8FAFC" }}>{service?.title}</h3>
                <p className="text-[14px] font-body leading-relaxed leading-[1.75]" style={{ color: "#94A3B8" }}>{service?.description}</p>
              </div>
              <div className="relative z-10 flex items-end justify-between mt-auto pt-5" style={{ borderTop: "1px solid rgba(255,255,255,0.07)" }}>
                <div>
                  <div className="stat-glow font-heading font-700 text-[20px] leading-none" style={{ color: service?.accent }}>{service?.stat}</div>
                  <div className="text-[11px] font-body mt-0.5" style={{ color: "#94A3B8" }}>{service?.statLabel}</div>
                </div>
                <div className="w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110" style={{ background: service?.accentBg, border: `1px solid ${service?.accent}40`, color: service?.accent }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"><path d="M7 17L17 7M17 7H7M17 7v10"/></svg>
                </div>
              </div>
            </Link>
          ))}
        </StaggerContainer>


      </div>
    </section>
  );
}